import { useRef, useEffect, useState } from 'react';
import { Calendar, ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/ui/button';

interface DateTimePickerProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  minDate?: Date; // 最小可选时间，默认不限制
}

export function DateTimePicker({
  value,
  onChange,
  placeholder = '请选择到期时间',
  className,
  disabled = false,
  minDate,
}: DateTimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(
    value ? new Date(value) : new Date()
  );
  const [currentMonth, setCurrentMonth] = useState<Date>(
    value ? new Date(value) : new Date()
  );
  const [hourOpen, setHourOpen] = useState(false);
  const [minuteOpen, setMinuteOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 当外部value变化时同步
  useEffect(() => {
    if (value) {
      setSelectedDate(new Date(value));
      setCurrentMonth(new Date(value));
    }
  }, [value]);

  // 处理点击外部关闭
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // 格式化日期为 datetime-local 格式
  const formatDatetime = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // 格式化显示文本
  const formatDisplay = (date: Date): string => {
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 获取当月的所有日期
  const getDaysInMonth = (date: Date): (number | null)[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (number | null)[] = [];

    // 添加前面的空白日期（上个月的日期）
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // 添加当月的日期
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  const days = getDaysInMonth(currentMonth);
  const monthYear = currentMonth.toLocaleString('zh-CN', {
    year: 'numeric',
    month: 'long',
  });

  const handleDateSelect = (day: number) => {
    const newDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
      selectedDate.getHours(),
      selectedDate.getMinutes()
    );
    setSelectedDate(newDate);
  };

  const handleTimeChange = (type: 'hour' | 'minute', value: number) => {
    const newDate = new Date(selectedDate);
    if (type === 'hour') {
      newDate.setHours(value);
    } else {
      newDate.setMinutes(value);
    }
    setSelectedDate(newDate);
  };

  const handleConfirm = () => {
    onChange(formatDatetime(selectedDate));
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
    if (value) {
      setSelectedDate(new Date(value));
      setCurrentMonth(new Date(value));
    }
  };

  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
    );
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (day: number) => {
    return (
      day === selectedDate.getDate() &&
      currentMonth.getMonth() === selectedDate.getMonth() &&
      currentMonth.getFullYear() === selectedDate.getFullYear()
    );
  };

  // 检查某个日期是否在最小日期之前（禁用）
  const isDateDisabled = (day: number): boolean => {
    if (!minDate) return false;
    const checkDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
      23,
      59,
      59
    );
    // 如果该日期的最后一秒都在minDate之前，则禁用
    return checkDate < minDate;
  };

  // 检查选中的日期是否是minDate的同一天
  const isMinDateDay = (): boolean => {
    if (!minDate) return false;
    return (
      selectedDate.getFullYear() === minDate.getFullYear() &&
      selectedDate.getMonth() === minDate.getMonth() &&
      selectedDate.getDate() === minDate.getDate()
    );
  };

  // 检查某个小时是否被禁用（仅当选中的是minDate当天时）
  const isHourDisabled = (hour: number): boolean => {
    if (!minDate || !isMinDateDay()) return false;
    return hour < minDate.getHours();
  };

  // 检查某个分钟是否被禁用（仅当选中的是minDate当天且是同一小时时）
  const isMinuteDisabled = (minute: number): boolean => {
    if (!minDate || !isMinDateDay()) return false;
    if (selectedDate.getHours() < minDate.getHours()) return true;
    if (selectedDate.getHours() > minDate.getHours()) return false;
    return minute < minDate.getMinutes();
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* 显示框 */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 border rounded-lg cursor-pointer transition-colors',
          'border-gray-300 hover:border-blue-500 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-100',
          'bg-white text-sm h-9',
          disabled && 'bg-gray-50 cursor-not-allowed opacity-60'
        )}
      >
        <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          {value ? (
            <span className="text-sm text-gray-700 truncate">
              {formatDisplay(selectedDate)}
            </span>
          ) : (
            <span className="text-sm text-gray-400">{placeholder}</span>
          )}
        </div>
      </div>

      {/* 下拉选择器 */}
      {isOpen && !disabled && (
        <div className="absolute top-full left-0 mt-2 z-50 bg-white border border-gray-300 rounded-lg shadow-lg p-3 w-[520px]">
          <div className="grid grid-cols-[1.5fr,1fr] gap-3">
            {/* 日期选择区域 */}
            <div className="space-y-3 border-r border-gray-100 pr-3">
              {/* 月份导航 */}
              <div className="flex items-center justify-between mb-1">
                <button
                  onClick={handlePrevMonth}
                  className="p-0.5 hover:bg-gray-100 rounded"
                >
                  <ChevronUp className="w-3 h-3" />
                </button>
                <span className="text-xs font-medium">{monthYear}</span>
                <button
                  onClick={handleNextMonth}
                  className="p-0.5 hover:bg-gray-100 rounded"
                >
                  <ChevronDown className="w-3 h-3" />
                </button>
              </div>

              {/* 周度标题 */}
              <div className="grid grid-cols-7 gap-0.5 mb-1">
                {['日', '一', '二', '三', '四', '五', '六'].map((day) => (
                  <div
                    key={day}
                    className="text-center text-xs font-medium text-gray-600 py-1"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* 日期网格 */}
              <div className="grid grid-cols-7 gap-0.5">
                {days.map((day, idx) => {
                  const dayDisabled = day === null || isDateDisabled(day);
                  return (
                    <button
                      key={idx}
                      onClick={() =>
                        day && !dayDisabled && handleDateSelect(day)
                      }
                      disabled={dayDisabled}
                      className={cn(
                        'p-1 text-xs rounded transition-colors',
                        day === null && 'cursor-default',
                        dayDisabled &&
                          day !== null &&
                          'text-gray-300 cursor-not-allowed',
                        !dayDisabled &&
                          day !== null &&
                          'hover:bg-gray-100 cursor-pointer',
                        isToday(day!) &&
                          !dayDisabled &&
                          'border border-gray-400',
                        isSelected(day!) &&
                          !dayDisabled &&
                          'bg-blue-500 text-white hover:bg-blue-600',
                        !isSelected(day!) &&
                          !isToday(day!) &&
                          !dayDisabled &&
                          day !== null &&
                          'text-gray-700'
                      )}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 时间选择区域 */}
            <div className="flex flex-col justify-between space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {/* 小时 */}
                <div className="relative">
                  <label className="text-xs font-medium text-gray-600 mb-1 block">
                    小时
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setHourOpen((prev) => !prev);
                      setMinuteOpen(false);
                    }}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm flex items-center justify-between hover:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    <span>
                      {String(selectedDate.getHours()).padStart(2, '0')}
                    </span>
                    <ChevronDown className="w-3 h-3 text-gray-500" />
                  </button>
                  {hourOpen && (
                    <div className="absolute mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-y-auto z-10">
                      <div className="flex flex-col divide-y divide-gray-100">
                        {Array.from({ length: 24 }, (_, i) => i).map((hour) => {
                          const hourDisabled = isHourDisabled(hour);
                          return (
                            <button
                              key={hour}
                              type="button"
                              disabled={hourDisabled}
                              onClick={() => {
                                if (!hourDisabled) {
                                  handleTimeChange('hour', hour);
                                  setHourOpen(false);
                                }
                              }}
                              className={cn(
                                'text-xs text-left px-3 py-2',
                                hourDisabled &&
                                  'text-gray-300 cursor-not-allowed',
                                !hourDisabled &&
                                  'hover:bg-blue-50 cursor-pointer',
                                selectedDate.getHours() === hour &&
                                  !hourDisabled &&
                                  'bg-blue-500 text-white'
                              )}
                            >
                              {hour.toString().padStart(2, '0')}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* 分钟 */}
                <div className="relative">
                  <label className="text-xs font-medium text-gray-600 mb-1 block">
                    分钟
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setMinuteOpen((prev) => !prev);
                      setHourOpen(false);
                    }}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm flex items-center justify-between hover:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    <span>
                      {String(selectedDate.getMinutes()).padStart(2, '0')}
                    </span>
                    <ChevronDown className="w-3 h-3 text-gray-500" />
                  </button>
                  {minuteOpen && (
                    <div className="absolute mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-y-auto z-10">
                      <div className="flex flex-col divide-y divide-gray-100">
                        {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(
                          (minute) => {
                            const minuteDisabled = isMinuteDisabled(minute);
                            return (
                              <button
                                key={minute}
                                type="button"
                                disabled={minuteDisabled}
                                onClick={() => {
                                  if (!minuteDisabled) {
                                    handleTimeChange('minute', minute);
                                    setMinuteOpen(false);
                                  }
                                }}
                                className={cn(
                                  'text-xs text-left px-3 py-2',
                                  minuteDisabled &&
                                    'text-gray-300 cursor-not-allowed',
                                  !minuteDisabled &&
                                    'hover:bg-blue-50 cursor-pointer',
                                  selectedDate.getMinutes() === minute &&
                                    !minuteDisabled &&
                                    'bg-blue-500 text-white'
                                )}
                              >
                                {minute.toString().padStart(2, '0')}
                              </button>
                            );
                          }
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 底部按钮 */}
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  className="text-xs h-8 px-3"
                >
                  取消
                </Button>
                <Button
                  onClick={handleConfirm}
                  size="sm"
                  className="text-xs h-8 px-3 bg-blue-500 hover:bg-blue-600 text-white"
                >
                  确定
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
